import React, { useEffect, useState } from "react";
import PageLayout from "@/components/layout/PageLayout";
import { Hero, Section, SectionHeader } from "@/components/layout/Sections";
import ContactForm from "@/components/ContactForm";
import { motion } from "framer-motion";
import { Briefcase, MapPin, Clock, ChevronRight } from "lucide-react";
import api from "@/lib/api";

function inlineFmt(text) {
  const parts = text.split(/(\*\*[^*]+\*\*)/g);
  return parts.map((p, i) => {
    const m = p.match(/^\*\*([^*]+)\*\*$/);
    if (m) return <strong key={i} className="font-semibold text-gray-900">{m[1]}</strong>;
    return <React.Fragment key={i}>{p}</React.Fragment>;
  });
}

export default function LavoraConNoi() {
  const [jobs, setJobs] = useState([]);
  const [selected, setSelected] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all"); // all | auto | moto

  useEffect(() => {
    api.get("/jobs")
      .then((r) => setJobs(r.data))
      .catch(() => setJobs([]))
      .finally(() => setLoading(false));
  }, []);

  const counts = {
    all: jobs.length,
    auto: jobs.filter((j) => j.section === "auto" || j.section === "generale").length,
    moto: jobs.filter((j) => j.section === "moto" || j.section === "generale").length,
  };
  const filtered = filter === "all"
    ? jobs
    : jobs.filter((j) => j.section === filter || j.section === "generale");

  return (
    <PageLayout>
      <Hero
        image="/showroom/auto-officina.jpg"
        eyebrow="Carriere · NSA Motors"
        title="Lavora con noi"
        subtitle={<>Unisciti alla squadra NSA Motors. Cerchiamo talenti appassionati<br />di motori, capaci di crescere insieme al nostro progetto.</>}
      />

      <Section>
        <SectionHeader
          eyebrow="Posizioni aperte"
          title="Le opportunità di oggi"
          description="Scopri le posizioni attualmente disponibili in NSA Motors. Se non trovi quella giusta per te ma pensi di poter contribuire al nostro team, mandaci comunque la tua candidatura spontanea nel form in fondo alla pagina."
        />

        {/* Filter tabs Auto / Moto */}
        {jobs.length > 0 && (
          <div className="mb-10 flex flex-wrap gap-2 items-center border-b border-gray-200">
            {[
              { k: "all", label: "Tutte" },
              { k: "auto", label: "Reparto Auto" },
              { k: "moto", label: "Reparto Moto" },
            ].map((tab) => (
              <button
                key={tab.k}
                type="button"
                onClick={() => setFilter(tab.k)}
                className={`label-eyebrow px-5 py-3 -mb-px border-b-2 transition-colors ${
                  filter === tab.k
                    ? "border-[color:var(--nsa-orange)] text-gray-900"
                    : "border-transparent text-gray-500 hover:text-gray-800"
                }`}
                data-testid={`jobs-filter-${tab.k}`}
              >
                {tab.label} <span className="ml-1 text-xs opacity-60">({counts[tab.k]})</span>
              </button>
            ))}
          </div>
        )}

        {loading ? (
          <div className="p-10 text-center text-gray-500" data-testid="jobs-loading">Caricamento…</div>
        ) : filtered.length === 0 ? (
          <div className="card-flat p-10 text-center text-gray-500" data-testid="jobs-empty">
            {jobs.length === 0
              ? "Al momento non ci sono posizioni aperte. Puoi comunque inviare una candidatura spontanea."
              : "Nessuna posizione aperta in questo reparto. Prova un altro filtro o invia una candidatura spontanea."}
          </div>
        ) : (
          <div className="grid md:grid-cols-2 gap-6" data-testid="jobs-list">
            {filtered.map((job, i) => (
              <motion.article
                key={job.id}
                initial={{ opacity: 0, y: 14 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.4, delay: (i % 2) * 0.05 }}
                className="card-flat p-8 flex flex-col cursor-pointer group hover:shadow-lg transition-shadow"
                onClick={() => setSelected(job)}
                data-testid={`job-card-${job.id}`}
              >
                <div className="flex items-start justify-between gap-4">
                  <div className="w-11 h-11 flex items-center justify-center bg-[color:var(--nsa-orange)]/10 text-[color:var(--nsa-orange)] shrink-0">
                    <Briefcase size={20} />
                  </div>
                  <span className="label-eyebrow text-white bg-gray-900 px-3 py-1">
                    {job.section === "auto" ? "Auto" : job.section === "moto" ? "Moto" : "Generale"}
                  </span>
                </div>
                <h3 className="font-display uppercase text-2xl md:text-3xl mt-6 leading-[1.05] text-gray-900">
                  {job.title}
                </h3>
                <div className="mt-4 flex flex-wrap gap-x-5 gap-y-1 text-sm text-gray-600">
                  {job.location && (
                    <span className="inline-flex items-center gap-1.5">
                      <MapPin size={14} /> {job.location}
                    </span>
                  )}
                  {job.contract_type && (
                    <span className="inline-flex items-center gap-1.5">
                      <Clock size={14} /> {job.contract_type}
                    </span>
                  )}
                </div>
                {job.description && (
                  <p className="text-gray-600 mt-5 line-clamp-3 leading-relaxed">
                    {job.description.replace(/[*#>-]/g, "").slice(0, 220)}
                  </p>
                )}
                <span className="mt-6 label-eyebrow text-[color:var(--nsa-orange)] inline-flex items-center gap-2 group-hover:gap-3 transition-all">
                  Scopri di più <ChevronRight size={14} />
                </span>
              </motion.article>
            ))}
          </div>
        )}
      </Section>

      {/* Detail modal */}
      {selected && (
        <div
          className="fixed inset-0 z-[80] bg-black/70 backdrop-blur-sm flex items-end sm:items-center justify-center p-0 sm:p-6 overflow-y-auto"
          onClick={() => setSelected(null)}
          data-testid="job-modal"
        >
          <motion.div
            initial={{ y: 40, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ duration: 0.25 }}
            onClick={(e) => e.stopPropagation()}
            className="bg-white w-full sm:max-w-2xl max-h-[92vh] overflow-y-auto shadow-2xl"
          >
            <div className="p-8 md:p-10">
              <div className="flex items-start justify-between gap-4">
                <div className="flex-1">
                  <span className="label-eyebrow text-[color:var(--nsa-orange)]">
                    {selected.section === "auto" ? "Reparto Auto" : selected.section === "moto" ? "Reparto Moto" : "NSA Motors"}
                    {selected.contract_type ? ` · ${selected.contract_type}` : ""}
                  </span>
                  <h2 className="font-display uppercase text-3xl md:text-4xl mt-2 leading-[1.05]">
                    {selected.title}
                  </h2>
                  {selected.location && (
                    <div className="mt-2 text-sm text-gray-600 inline-flex items-center gap-1.5">
                      <MapPin size={14} /> {selected.location}
                    </div>
                  )}
                </div>
                <button
                  type="button"
                  onClick={() => setSelected(null)}
                  className="h-10 w-10 flex items-center justify-center border border-gray-300 hover:bg-gray-100"
                  aria-label="Chiudi"
                  data-testid="job-modal-close"
                >
                  ✕
                </button>
              </div>

              <div className="mt-8 space-y-4 text-gray-700 leading-relaxed">
                {selected.description.split(/\n\n+/).map((p, i) => (
                  <p key={i}>{inlineFmt(p.trim())}</p>
                ))}
              </div>

              {selected.requirements?.length > 0 && (
                <div className="mt-8">
                  <span className="label-eyebrow text-gray-500">Requisiti</span>
                  <ul className="mt-3 space-y-2">
                    {selected.requirements.map((r, i) => (
                      <li key={i} className="flex items-start gap-2 text-gray-700">
                        <ChevronRight size={16} className="text-[color:var(--nsa-orange)] mt-1 shrink-0" />
                        <span>{inlineFmt(r)}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              <div className="mt-10 bg-gray-50 p-6 md:p-8 border-l-4 border-[color:var(--nsa-orange)]">
                <h4 className="font-display uppercase text-xl mb-1">Candidati ora</h4>
                <p className="text-sm text-gray-600">
                  Compila il form qui sotto — riceverai risposta entro pochi giorni lavorativi.
                </p>
                <div className="mt-6">
                  <ContactForm
                    section="generale"
                    defaultSubject={`Candidatura — ${selected.title}`}
                    submitLabel="Invia candidatura"
                    tid="job-apply-form"
                  />
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      )}

      {/* Spontaneous application form */}
      <Section tone="dark">
        <div className="grid md:grid-cols-2 gap-10 md:gap-16 items-start">
          <div>
            <span className="label-eyebrow text-[color:var(--nsa-orange)]">Candidatura spontanea</span>
            <h2 className="font-display uppercase text-4xl md:text-5xl mt-3 leading-[0.95]">
              Non trovi la<br />tua posizione?
            </h2>
            <p className="mt-6 text-white/80 leading-relaxed max-w-md">
              Se pensi di poter dare valore al nostro team, mandaci comunque il tuo profilo:
              lo teniamo in considerazione per le prossime opportunità.
            </p>
          </div>
          <div className="bg-white p-6 md:p-8">
            <ContactForm
              section="generale"
              defaultSubject="Candidatura spontanea"
              submitLabel="Invia candidatura"
              tid="spontaneous-application"
            />
          </div>
        </div>
      </Section>
    </PageLayout>
  );
}
